import { AnyEntityId } from "@mapmycustomers/shared/types/entity";

import { BulkDeleteResponse, BulkOperationFailedRecord } from "@app/store/api/ApiService";

export interface BulkDeleteOutcome {
  // ids platform confirmed as deleted, hence safe to report and to sync other features with
  deletedIds: AnyEntityId[];
  failedCount: number;
  failedRecords: BulkOperationFailedRecord[];
}

/**
 * Interprets a bulk-delete response. Platform only answers with a [deletedIds, failedRecords]
 * tuple when $process=any is requested, so any other shape means there is nothing to report as
 * failed. Deletions are taken from platform's confirmed id list rather than derived by
 * subtracting the failures: a record platform does not account for is not a record we can call
 * deleted, and a partial delete claiming full success is the failure mode this flow exists to
 * avoid. Contacts and deals validate existence against ES while the delete itself hits Postgres,
 * so an id really can come back in neither list.
 */
const parseBulkDeleteOutcome = (
  requestedIds: AnyEntityId[],
  response: unknown
): BulkDeleteOutcome => {
  const isTuple =
    Array.isArray(response) && Array.isArray(response[0]) && Array.isArray(response[1]);
  if (!isTuple) {
    return { deletedIds: requestedIds, failedCount: 0, failedRecords: [] };
  }

  const [confirmedIds, failedRecords] = response as BulkDeleteResponse;
  const confirmedIdSet = new Set<AnyEntityId>(confirmedIds);
  const deletedIds = requestedIds.filter((id) => confirmedIdSet.has(id));

  return {
    deletedIds,
    // counted against the requested ids so duplicates in a selection can't skew it
    failedCount: requestedIds.length - deletedIds.length,
    failedRecords,
  };
};

export default parseBulkDeleteOutcome;
